import classNames from 'classnames';
import { useSearchBox } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useRemoveMarketFilterChipEvent } from 'hooks/events/useRemoveMarketFilterChipEvent';
import {
  isListFilterWidget,
  isRangeFilterWidget,
  useFormatFilterWidgetValue,
} from 'hooks/search/useFormatFilterWidgetValue';
import { useRelevantRefinements } from 'hooks/search/useRelevantRefinements';
import { useClearAllFilters } from 'search/ClearAllFilters';
import { useFiltersCount } from 'search/FiltersManager/useFiltersCount';

import { FilterChip } from './FilterChip';
import { ListActiveFilter } from './ListActiveFilter';
import { RangeActiveFilter } from './RangeActiveFilter';
import { VirtualToggleActiveFilters } from './VirtualToggleActiveFilters';
import { RefinementItem } from './types';

const Container = styled.div`
  display: flex;
  gap: var(--unit);
  flex-wrap: nowrap;
  overflow-x: auto;
  & > * {
    flex: none;
  }
  @media ${tabletAndAbove} {
    flex-wrap: wrap;
    & > * {
      flex: initial;
    }
    &.forceScroll {
      flex-wrap: nowrap;
      & > * {
        flex: none;
      }
    }
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ActiveFilter = ({ item }: { item: RefinementItem }) => {
  const { sport } = useSportContext();
  const trackRemoveMarketFilterChip = useRemoveMarketFilterChipEvent();
  const { formatFilterWidgetValue } = useFormatFilterWidgetValue();

  if (
    [
      TOGGLE_FILTERS.creditEligibleFilter.attribute,
      TOGGLE_FILTERS.onSaleFilter.attribute,
    ].includes(item.attribute)
  ) {
    const [label] = formatFilterWidgetValue(item);

    return (
      <FilterChip
        label={label}
        onClose={() => {
          item.refine(item.refinements?.[0]);
          if (sport) {
            trackRemoveMarketFilterChip(item.attribute, 'false', sport);
          }
        }}
      />
    );
  }

  if (isListFilterWidget(item)) {
    return <ListActiveFilter item={item} />;
  }

  if (isRangeFilterWidget(item)) {
    return <RangeActiveFilter item={item} />;
  }

  return null;
};

export const ActiveFilters = ({ forceScroll }: { forceScroll?: boolean }) => {
  const { sport } = useSportContext();
  const trackRemoveMarketFilterChip = useRemoveMarketFilterChipEvent();
  const filtersCount = useFiltersCount();
  const items = useRelevantRefinements();
  const { query, clear } = useSearchBox();
  const clearAllFilters = useClearAllFilters();
  const setVirtualToggleFilters = useVirtualToggleManager();

  if (!filtersCount) {
    return null;
  }

  return (
    <Container className={classNames({ forceScroll })}>
      {query && (
        <FilterChip
          label={
            <FormattedMessage
              id="ActiveFilters.search"
              defaultMessage="Search: {query}"
              values={{ query }}
            />
          }
          onClose={() => {
            clear();
            if (sport) {
              trackRemoveMarketFilterChip('query', query, sport);
            }
          }}
        />
      )}
      {items.map(item => (
        <ActiveFilter key={item.attribute} item={item as RefinementItem} />
      ))}
      <VirtualToggleActiveFilters
        setVirtualToggleFilters={setVirtualToggleFilters}
      />
      <Button size="small" onClick={clearAllFilters} color="red">
        <FormattedMessage {...filters.clearAll} />
      </Button>
    </Container>
  );
};
